import { useEffect, useState, type ReactNode } from 'react';
import { onAuthStateChanged, type User as FirebaseUser } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import type { User } from '../lib/types';
import { AuthContext, type AuthState } from './authContext';

interface ProfileState {
  uid: string;
  profile: User | null;
}

/** Tracks the auth user and live-subscribes to users/{uid} so approvals/deactivations apply instantly. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [authReady, setAuthReady] = useState(false);
  // Tagged with the uid it belongs to, so switching users never shows a stale profile.
  const [profileState, setProfileState] = useState<ProfileState | null>(null);

  useEffect(
    () =>
      onAuthStateChanged(auth, (u) => {
        setUser(u);
        setAuthReady(true);
      }),
    [],
  );

  useEffect(() => {
    if (!user) return;
    const uid = user.uid;
    // During sign-up the doc is written right after the auth user, so give it a moment
    // before treating a missing profile as final.
    const giveUp = setTimeout(
      () => setProfileState((s) => (s?.uid === uid ? s : { uid, profile: null })),
      8000,
    );
    const unsubscribe = onSnapshot(
      doc(db, 'users', uid),
      (snap) => {
        if (!snap.exists()) return;
        clearTimeout(giveUp);
        setProfileState({
          uid,
          profile: {
            ...(snap.data({ serverTimestamps: 'estimate' }) as Omit<User, 'id'>),
            id: snap.id,
          },
        });
      },
      () => {
        clearTimeout(giveUp);
        setProfileState({ uid, profile: null });
      },
    );
    return () => {
      clearTimeout(giveUp);
      unsubscribe();
    };
  }, [user]);

  const profileReady = !!user && profileState?.uid === user.uid;
  const value: AuthState = {
    user,
    profile: profileReady ? profileState.profile : null,
    loading: !authReady || (!!user && !profileReady),
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
